import type React from "react";
import Button from "~/components/ui/button";
import { CustomCheckbox } from "~/components/ui/check_Box";
import { CustomRadio } from "~/components/ui/radioBox";
import styles from "../../index.module.scss";
import type { FieldComponentProps, OptionItem } from "../../types";

const CheckBoxOptions: React.FC<FieldComponentProps> = ({
  options,
  onChange,
  isReadOnly = false,
}) => {
  const handleBooleanOptionChange = (key: string) => (checked: boolean) => {
    onChange(key, checked);
  };

  const handleRadioChange = (key: string) => (value: string) => {
    onChange(key, value);
  };

  // options.options が配列の場合のみ使用し、それ以外は空配列にする
  const optionItems: OptionItem[] = Array.isArray(options.options) ? options.options : [];
  // 選択された初期値のIDリスト（存在しない場合は空配列）
  const selectedDefaultOptions = options.defaultSelectedOptions || [];

  // 選択肢を追加する関数
  const addOption = () => {
    // 新しい選択肢のIDを生成（最後のIDに1を足す、または1から始める）
    const newId =
      optionItems.length > 0
        ? (parseInt(optionItems[optionItems.length - 1]!.id, 10) + 1).toString()
        : "1";

    const newOptions = [...optionItems, { id: newId, value: `新しい選択肢${newId}` }];
    onChange("options", newOptions);
  };

  // 選択肢を削除する関数
  const removeOption = (id: string) => {
    const newOptions = optionItems.filter((item) => item.id !== id);
    onChange("options", newOptions);

    // 削除された選択肢が初期値に選択されていれば、それも削除
    if (selectedDefaultOptions.includes(id)) {
      const newDefaultOptions = selectedDefaultOptions.filter((optionId) => optionId !== id);
      onChange("defaultSelectedOptions", newDefaultOptions);
    }
  };

  // 選択肢の値を変更する関数
  const handleOptionValueChange = (id: string, newValue: string) => {
    const newOptions = optionItems.map((item) =>
      item.id === id ? { ...item, value: newValue } : item,
    );
    onChange("options", newOptions);
  };

  // 選択肢を上に移動する関数
  const moveOptionUp = (id: string) => {
    const index = optionItems.findIndex((item) => item.id === id);
    if (index > 0) {
      const newItems = [...optionItems];
      [newItems[index - 1], newItems[index]] = [newItems[index]!, newItems[index - 1]!];
      onChange("options", newItems);
    }
  };

  // 選択肢を下に移動する関数
  const moveOptionDown = (id: string) => {
    const index = optionItems.findIndex((item) => item.id === id);
    if (index < optionItems.length - 1) {
      const newItems = [...optionItems];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1]!, newItems[index]!];
      onChange("options", newItems);
    }
  };

  // 初期値の選択状態を切り替える関数
  const toggleDefaultOption = (id: string) => {
    let newDefaultOptions: string[];

    if (selectedDefaultOptions.includes(id)) {
      // 既に選択されている場合は削除
      newDefaultOptions = selectedDefaultOptions.filter((optionId) => optionId !== id);
    } else {
      // まだ選択されていない場合は追加
      newDefaultOptions = [...selectedDefaultOptions, id];
    }

    onChange("defaultSelectedOptions", newDefaultOptions);
  };

  // 選択肢リストを表示するコンポーネント
  const renderOptionsList = () => {
    return (
      <div className={styles["form-group"]}>
        <label>項目と順番</label>
        <div className={styles["options-list"]}>
          {optionItems.map((item) => (
            <div key={item.id} className={styles["option-item"]}>
              <div className={styles["option-controls"]}>
                <button
                  type="button"
                  onClick={() => moveOptionUp(item.id)}
                  disabled={isReadOnly}
                  className={styles["move-button"]}
                >
                  ▲
                </button>
                <button
                  type="button"
                  onClick={() => moveOptionDown(item.id)}
                  disabled={isReadOnly}
                  className={styles["move-button"]}
                >
                  ▼
                </button>
              </div>
              <input
                type="text"
                value={item.value}
                onChange={(e) => handleOptionValueChange(item.id, e.target.value)}
                disabled={isReadOnly}
                className={styles["option-input"]}
              />
              <button
                type="button"
                onClick={() => removeOption(item.id)}
                disabled={isReadOnly}
                className={styles["remove-button"]}
              >
                ー
              </button>
              <div className={styles["default-checkbox"]}>
                <input
                  type="checkbox"
                  checked={selectedDefaultOptions.includes(item.id)}
                  onChange={() => toggleDefaultOption(item.id)}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          ))}
        </div>
        {!isReadOnly && <Button label="＋" onClick={addOption} styleType="white" />}
      </div>
    );
  };

  // 選択した初期値を表示するコンポーネント
  const renderSelectedDefaultValues = () => {
    const selectedItems = optionItems.filter((item) => selectedDefaultOptions.includes(item.id));

    if (selectedItems.length === 0) {
      return <div className={styles["selected-defaults-empty"]}>選択された初期値はありません</div>;
    }

    return (
      <div className={styles["selected-defaults"]}>
        {selectedItems.map((item) => (
          <div key={item.id} className={styles["selected-default-item"]}>
            {item.value}
          </div>
        ))}
      </div>
    );
  };

  const orderOptions = [
    { value: "VERTICAL", label: "縦" },
    { value: "HORIZONTAL", label: "横" },
  ];

  return (
    <div className={styles["optionSection"]}>
      <div className={styles["optionGroup"]}>
        <h4>基本設定</h4>
        <div className={styles["checkboxOption"]}>
          <CustomCheckbox
            label="必須項目にする"
            checked={options.required === true}
            onChange={handleBooleanOptionChange("required")}
            disabled={isReadOnly}
          />
        </div>
        <div className={styles["checkboxOption"]}>
          <CustomCheckbox
            label="フィールド名を表示しない"
            checked={options.noLabel === true}
            onChange={handleBooleanOptionChange("noLabel")}
            disabled={isReadOnly}
          />
        </div>
      </div>

      <div className={styles["optionGroup"]}>
        <h4>選択肢の並び</h4>
        <div className={styles["radioGroup"]}>
          <div className={styles["radioOptions"]}>
            {orderOptions.map((option) => (
              <CustomRadio
                key={option.value}
                name="order"
                label={option.label}
                value={option.value}
                checked={options.order === option.value}
                onChange={handleRadioChange("order")}
                disabled={isReadOnly}
              />
            ))}
          </div>
        </div>
      </div>

      <div className={styles["optionGroup"]}>
        <h4>選択肢の管理</h4>
        {renderOptionsList()}
      </div>

      <div className={styles["optionGroup"]}>
        <h4>初期値</h4>
        <p className={styles["help-text"]}>
          選択肢リストの右側のチェックボックスで初期値として選択される項目を指定できます
        </p>
        {renderSelectedDefaultValues()}
      </div>
    </div>
  );
};

export default CheckBoxOptions;
